import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Drawer,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import MapIcon from '@material-ui/icons/Map'
import TableChartIcon from '@material-ui/icons/TableChart'
import ListIcon from '@material-ui/icons/List'

import TopRail from '../DashboardV2/TopRail'
import PageHeader from '../DashboardV2/PageHeader'
import { IconCheck } from '../DashboardV2/icons'
import PropertyDetailDrawer from './PropertyDetailDrawer'
import { applyBasemapProvider, googleHybridProvider } from '../../HerbicideSchedule/v3/maricopaBasemap'
import '../../../styles/hmiReskin.css'
import '../DashboardV2/dashboardV2.css'
import './propertyOverview.css'

const V4_T = {
  green: '#1f5e3a',
  rust: '#a9341d',
  amber: '#d8973a',
  plum: '#6e3fb7',
  blue: '#3454c1',
  ink: '#1c2419',
  mute: '#6f7a6b',
  line: '#e7dfc9',
  cream: '#f4efe4',
  surface: '#fbf8f1'
}

// Helpers
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatCompactCurrency = (value) => {
  const num = Number(value) || 0
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}k`
  return formatCurrency(num)
}

function Sparkline ({ values, width = 60, height = 20, color = V4_T.green, fill = true }) {
  const cleanValues = values && values.length ? values.map(v => Number(v) || 0) : [0, 0, 0, 0]
  const yMin = Math.min(...cleanValues)
  const yMax = Math.max(...cleanValues)
  const range = yMax - yMin || 1
  const xStep = cleanValues.length > 1 ? (width - 2) / (cleanValues.length - 1) : width - 2
  const points = cleanValues.map((value, index) => [
    1 + index * xStep,
    height - 2 - ((value - yMin) / range) * (height - 4)
  ])
  const path = points.map((point, index) => `${index ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ')
  const fillPath = `${path} L ${points[points.length - 1][0].toFixed(1)} ${height - 1} L ${points[0][0].toFixed(1)} ${height - 1} Z`

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {fill && <path d={fillPath} fill={color} opacity="0.12" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2" fill={color} stroke="#fff" strokeWidth="1" />
    </svg>
  )
}

const useStyles = makeStyles(theme => ({
  drawer: {
    width: 420,
    maxWidth: '100vw'
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: '#fff',
      height: 36,
      fontSize: 13
    }
  },
  crewSelect: {
    minWidth: 140,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: '#fff',
      height: 36,
      fontSize: 13
    }
  }
}))

function PropertyOverview(props) {
  const { url, user, employees, tasks } = props
  const classes = useStyles()
  
  const [loading, setLoading] = useState(true)
  const [properties, setProperties] = useState([])
  const [viewMode, setViewMode] = useState('table') // 'table', 'list', 'map'
  const [searchTerm, setSearchTerm] = useState('')
  const [crewFilter, setCrewFilter] = useState('all')
  const [activeFilter, setActiveFilter] = useState(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  
  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('netProfit')

  const fetchProperties = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${url}/api/aspire/routing/properties`)
      setProperties(res.data)
    } catch (err) {
      console.error('Failed to fetch properties:', err)
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => {
    fetchProperties()
  }, [fetchProperties])

  // Stats Calculations
  const stats = useMemo(() => {
    const total = properties.length
    const healthy = properties.filter(p => p.profitMargin >= 30).length
    const watch = total - healthy
    const losingMoney = properties.filter(p => p.netProfit < 0)
    const losingCount = losingMoney.length
    const negativeNetValue = losingMoney.reduce((sum, p) => sum + (p.netProfit || 0), 0)
    
    const underBid = properties.filter(p => (p.estimationVariance || 0) < 0)
    const underBidCount = underBid.length
    const hoursOverEstimate = Math.abs(underBid.reduce((sum, p) => sum + (p.estimationVariance || 0), 0))
    
    const totalOT = properties.reduce((sum, p) => sum + (p.totalOTHours || 0), 0)
    const totalRev = properties.reduce((sum, p) => sum + (p.totalEarnedRevenue || 0), 0)
    const totalNet = properties.reduce((sum, p) => sum + (p.netProfit || 0), 0)
    const blendedMargin = totalRev > 0 ? (totalNet / totalRev) * 100 : 0
    
    const herbFlags = properties.filter(p => p.herbicideFutureVisits === 0 && p.targetAnnualVisits > 0).length // Mock logic
    const irrigFlags = properties.filter(p => (p.syncHealth?.issues || []).includes('no-board-visits')).length

    return {
      total, healthy, watch,
      losingCount, negativeNetValue,
      underBidCount, hoursOverEstimate,
      totalOT, herbFlags, irrigFlags,
      totalRev, totalNet, blendedMargin
    }
  }, [properties])

  const attentionProperties = useMemo(() => {
    return [...properties]
      .sort((a, b) => (a.netProfit || 0) - (b.netProfit || 0))
      .slice(0, 4)
  }, [properties])

  const filteredProperties = useMemo(() => {
    let filtered = properties.filter(p => 
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.address1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(p.propertyId).includes(searchTerm)
    )

    if (crewFilter !== 'all') {
      filtered = filtered.filter(p => p.primaryCrewLeader?.name === crewFilter)
    }

    if (activeFilter === 'losing') filtered = filtered.filter(p => p.netProfit < 0)
    if (activeFilter === 'underbid') filtered = filtered.filter(p => (p.estimationVariance || 0) < 0)
    if (activeFilter === 'ot') filtered = filtered.filter(p => (p.totalOTHours || 0) > 0)
    // Add other filters as needed

    return filtered.sort((a, b) => {
      let aVal = a[orderBy]
      let bVal = b[orderBy]
      
      // Handle special cases
      if (orderBy === 'crew') {
        aVal = a.primaryCrewLeader?.name || ''
        bVal = b.primaryCrewLeader?.name || ''
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      if (order === 'asc') {
        return aVal < bVal ? -1 : 1
      } else {
        return aVal > bVal ? -1 : 1
      }
    })
  }, [properties, searchTerm, crewFilter, activeFilter, orderBy, order])

  const handleRowClick = (propertyId) => {
    setSelectedPropertyId(propertyId)
    setDrawerOpen(true)
  }

  const crews = useMemo(() => {
    const names = properties.map(p => p.primaryCrewLeader?.name).filter(Boolean)
    return ['all', ...new Set(names)].sort()
  }, [properties])

  return (
    <div className="property-overview-root">
      <TopRail url={url} user={user} employees={employees} tasks={tasks} />
      
      <div className="property-overview-content">
        <header className="property-header">
          <div className="property-breadcrumb">ALL PORTFOLIO TERRITORIES</div>
          <h1 className="property-title">Properties</h1>
        </header>

        <section className="stat-cards-grid">
          <StatCard label="Properties" value={stats.total} sub={`${stats.healthy} healthy · ${stats.watch} watch`} />
          <StatCard label="Losing Money" value={stats.losingCount} sub={`${formatCompactCurrency(stats.negativeNetValue)} net YTD`} />
          <StatCard label="Under-Bid" value={stats.underBidCount} sub={`${stats.hoursOverEstimate.toFixed(0)}h over estimate`} />
          <StatCard label="Overtime" value={`${stats.totalOT.toFixed(0)}h`} sub="YTD across territory" />
          <StatCard label="Herb / Irrig Flags" value={stats.herbFlags + stats.irrigFlags} sub={`${stats.herbFlags} herbicide · ${stats.irrigFlags} irrigation`} />
          <StatCard label="Revenue" value={formatCompactCurrency(stats.totalRev)} sub="total customer value" />
          <StatCard label="Net Profit" value={formatCompactCurrency(stats.totalNet)} sub={`${stats.blendedMargin.toFixed(1)}% blended margin`} />
        </section>

        <section className="needs-attention-section">
          <div className="section-label">Needs Attention</div>
          <div className="attention-cards-scroll">
            {attentionProperties.map(p => (
              <div key={p.propertyId} className="attention-card" onClick={() => handleRowClick(p.propertyId)} style={{ cursor: 'pointer' }}>
                <div className="attention-card-header">
                  <span className="attention-card-title">{p.name}</span>
                  <span className="attention-card-value">{formatCurrency(p.netProfit)}</span>
                </div>
                <div className="attention-card-desc">
                  Losing {formatCurrency(Math.abs(p.netProfit))} a year — actual hours run {(p.avgOnSiteHours / (p.estimatedHours || 1)).toFixed(1)}x estimate
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="filter-chips-row">
          <FilterChip label={`Losing money ${stats.losingCount}`} active={activeFilter === 'losing'} onClick={() => setActiveFilter(activeFilter === 'losing' ? null : 'losing')} />
          <FilterChip label={`Under-bid ${stats.underBidCount}`} active={activeFilter === 'underbid'} onClick={() => setActiveFilter(activeFilter === 'underbid' ? null : 'underbid')} />
          <FilterChip label={`OT offenders ${properties.filter(p => p.totalOTHours > 0).length}`} active={activeFilter === 'ot'} onClick={() => setActiveFilter(activeFilter === 'ot' ? null : 'ot')} />
          
          <Box flex={1} />
          
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Search name, address, #id..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              )
            }}
          />

          <FormControl variant="outlined" size="small" className={classes.crewSelect}>
            <Select
              value={crewFilter}
              onChange={(e) => setCrewFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">All crews</MenuItem>
              {crews.filter(c => c !== 'all').map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <div className="view-toggle" style={{ display: 'flex', background: '#e7dfc9', padding: 2, borderRadius: 8 }}>
            <IconButton size="small" onClick={() => setViewMode('table')} style={{ background: viewMode === 'table' ? '#fff' : 'transparent', borderRadius: 6 }}>
              <TableChartIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setViewMode('list')} style={{ background: viewMode === 'list' ? '#fff' : 'transparent', borderRadius: 6 }}>
              <ListIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setViewMode('map')} style={{ background: viewMode === 'map' ? '#fff' : 'transparent', borderRadius: 6 }}>
              <MapIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className="property-table-container">
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={8}>
              <CircularProgress color="primary" />
            </Box>
          ) : viewMode === 'map' ? (
            <div style={{ height: 600 }}>
              <PropertyMap properties={filteredProperties} onSelectProperty={handleRowClick} />
            </div>
          ) : viewMode === 'list' ? (
            <div className="property-list-view" style={{ padding: 24, overflowY: 'auto' }}>
              {filteredProperties.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#97a094' }}>No properties match the current filters.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                  <ListSection 
                    title="Losing Money" 
                    count={filteredProperties.filter(p => p.netProfit < 0).length}
                    properties={filteredProperties.filter(p => p.netProfit < 0)}
                    onSelect={handleRowClick}
                  />
                  <ListSection 
                    title="Under-Bid" 
                    count={filteredProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) < 0).length}
                    properties={filteredProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) < 0)}
                    onSelect={handleRowClick}
                  />
                  <ListSection 
                    title="Healthy" 
                    count={filteredProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) >= 0).length}
                    properties={filteredProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) >= 0)}
                    onSelect={handleRowClick}
                  />
                </div>
              )}
            </div>
          ) : (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 40 }}></TableCell>
                  <SortHeader label="Property" id="name" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                  <SortHeader label="Crew · Day" id="crew" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                  <TableCell>Services</TableCell>
                  <TableCell>Programs</TableCell>
                  <SortHeader label="Visits" id="completedOccurrences" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                  <SortHeader label="Hrs / Visit" id="avgOnSiteHours" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                  <SortHeader label="OT" id="totalOTHours" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                  <SortHeader label="Revenue" id="totalEarnedRevenue" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                  <SortHeader label="Net Profit" id="netProfit" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                  <SortHeader label="Margin · 12 Mo" id="profitMargin" current={orderBy} order={order} onSort={(id) => { setOrderBy(id); setOrder(order === 'asc' ? 'desc' : 'asc') }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProperties.map(p => {
                  const marginColor = p.profitMargin < 30 ? V4_T.rust : V4_T.green
                  const visitProgress = p.targetAnnualVisits > 0 ? (p.completedOccurrences / p.targetAnnualVisits) * 100 : 0
                  const hrDelta = (p.avgOnSiteHours || 0) - (p.estimatedHours || 0)
                  
                  return (
                    <TableRow key={p.propertyId} hover className="property-row" onClick={() => handleRowClick(p.propertyId)} style={{ cursor: 'pointer' }}>
                      <TableCell align="center">
                        <Tooltip title={p.profitMargin < 30 ? 'Unhealthy - Saguaro' : 'Healthy - Agave'}>
                          <span style={{ color: marginColor, fontSize: 18, cursor: 'help' }}>
                            {p.profitMargin < 30 ? '🌵' : '🌿'}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: 800, color: V4_T.ink, fontSize: 13 }}>{p.name}</div>
                        <div style={{ color: V4_T.mute, fontSize: 11 }}>{p.address1} · #{p.propertyId}</div>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: V4_T.green }} />
                          <span style={{ fontWeight: 600, fontSize: 12 }}>{p.primaryCrewLeader?.name || '-'}</span>
                          <span style={{ color: V4_T.mute, fontSize: 12 }}>· {p.scheduledDays?.[0]?.slice(0, 3) || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="service-progress-bar">
                          <div className="service-progress-fill" style={{ width: `${Math.min(100, visitProgress)}%`, background: visitProgress > 100 ? V4_T.amber : V4_T.green }} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {p.herbicideCompletedVisits > 0 && <span className="program-chip program-chip-h" title={`${p.herbicideCompletedVisits} herbicide visits done`}>H ✓</span>}
                          {p.irrigationCompletedVisits > 0 && <span className="program-chip program-chip-i" title={`${p.irrigationCompletedVisits} irrigation visits done`}>I ✓</span>}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, fontWeight: 600 }}>
                        {p.completedOccurrences} / {p.targetAnnualVisits || 0}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontFamily: 'var(--f-mono)', fontSize: 12 }}>
                          <span style={{ fontWeight: 800 }}>{(p.avgOnSiteHours || 0).toFixed(1)}</span>
                          <span style={{ color: V4_T.mute, fontSize: 10 }}>/ {(p.estimatedHours || 0).toFixed(1)}</span>
                          <span className={`delta-value ${hrDelta > 0 ? 'delta-pos' : 'delta-neg'}`}>
                            {hrDelta > 0 ? '+' : ''}{hrDelta.toFixed(1)}h
                          </span>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: (p.totalOTHours || 0) > 0 ? V4_T.rust : V4_T.mute }}>
                        {(p.totalOTHours || 0).toFixed(1)}h
                      </TableCell>
                      <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, fontWeight: 600 }}>
                        {formatCompactCurrency(p.totalEarnedRevenue)}
                      </TableCell>
                      <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, fontWeight: 800, color: (p.netProfit || 0) < 0 ? V4_T.rust : V4_T.green }}>
                        {formatCurrency(p.netProfit)}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 800, color: marginColor, fontSize: 12, minWidth: 40, textAlign: 'right' }}>
                            {p.profitMargin?.toFixed(1)}%
                          </span>
                          <div className="sparkline-container">
                             <Sparkline values={[25, 28, 32, p.profitMargin]} color={marginColor} />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
        
        <footer style={{ padding: '16px 0', fontSize: 11, color: '#97a094', display: 'flex', justifyContent: 'space-between' }}>
          <div>{stats.healthy} healthy · {stats.watch} watch · 8 critical</div>
          <div>synced from Aspire {user?.lastSyncTime || 'recently'}</div>
        </footer>
      </div>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{ paper: classes.drawer }}
      >
        <PropertyDetailDrawer 
          propertyId={selectedPropertyId} 
          onClose={() => setDrawerOpen(false)} 
          url={url}
        />
      </Drawer>
    </div>
  )
}

function StatCard({ label, value, sub }) {
  return (
    <div className="property-stat-card">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-sub">{sub}</div>
    </div>
  )
}

function FilterChip({ label, active, onClick }) {
  return (
    <button className={`filter-chip ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
    </button>
  )
}

function SortHeader({ label, id, current, order, onSort }) {
  const active = current === id
  return (
    <TableCell>
      <TableSortLabel
        active={active}
        direction={active ? order : 'asc'}
        onClick={() => onSort(id)}
        style={{ fontWeight: 800, fontSize: 11, textTransform: 'uppercase', color: '#97a094' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )
}

function PropertyMap({ properties, onSelectProperty }) {
  const mapRef = React.useRef(null)
  const tileLayerRef = React.useRef(null)
  
  useEffect(() => {
    if (!mapRef.current) {
      const map = L.map('property-overview-map').setView([33.4484, -112.0740], 10)
      applyBasemapProvider({ map, tileLayerRef, provider: googleHybridProvider })
      mapRef.current = map
    }
    
    const map = mapRef.current
    
    // Clear existing markers
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        map.removeLayer(layer)
      }
    })
    
    const markers = []
    properties.forEach(p => {
      if (p.latitude && p.longitude) {
        const marginColor = (p.profitMargin || 0) < 30 ? V4_T.rust : V4_T.green
        const marker = L.circleMarker([p.latitude, p.longitude], {
          radius: 6,
          fillColor: marginColor,
          color: '#fff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8
        })
          .bindPopup(`<b>${p.name}</b><br/>Margin: ${p.profitMargin?.toFixed(1)}%`)
          .on('click', () => onSelectProperty(p.propertyId))
          .addTo(map)
        markers.push(marker)
      }
    })
    
    if (markers.length > 0) {
      const group = new L.featureGroup(markers)
      map.fitBounds(group.getBounds().pad(0.1))
    }
  }, [properties, onSelectProperty])
  
  return <div id="property-overview-map" style={{ width: '100%', height: '100%', borderRadius: 12 }} />
}

function ListSection({ title, count, properties, onSelect }) {
  if (count === 0) return null
  return (
    <div className="list-section">
      <div className="section-label" style={{ marginBottom: 16 }}>{title} · {count}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {properties.map(p => (
          <div key={p.propertyId} className="attention-card" onClick={() => onSelect(p.propertyId)} style={{ cursor: 'pointer' }}>
            <div className="attention-card-header">
              <span className="attention-card-title">{p.name}</span>
              <span className="attention-card-value" style={{ color: p.netProfit < 0 ? V4_T.rust : V4_T.green }}>{formatCurrency(p.netProfit)}</span>
            </div>
            <div style={{ fontSize: 11, color: V4_T.mute, marginBottom: 8 }}>{p.address1}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: V4_T.green }} />
                <span style={{ fontWeight: 600, fontSize: 12 }}>{p.primaryCrewLeader?.name || '-'}</span>
              </div>
              <div style={{ fontWeight: 800, color: p.profitMargin < 30 ? V4_T.rust : V4_T.green, fontSize: 13 }}>
                {p.profitMargin?.toFixed(1)}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  employees: state.employees,
  tasks: state.tasks,
})

export default connect(mapStateToProps)(PropertyOverview)
